import { COORDINATE_STOCKHOLM } from '@shared/constants';
import { BuildingDesign } from '@shared/models/design/building-design.model';
import { omitUndefined } from '../../object.helpers';
import { createBuilding, FactoryBuilding } from './building.factory';
import { createStoreys } from './storey.factory';
import { applyMissingCalculatedAreaActivities } from '../calculated-area-activities.helpers';
import { v4 } from 'uuid';
import { withPersistentBuildingNames } from '../building-name.helpers';

/**
 * Factory input for a {@link BuildingDesign}. Accepts the legacy `storeys`
 * convenience (wrapped into a single {@link Building}) or explicit `buildings`.
 * Building entries may be {@link FactoryBuilding} partials — ids and storeys
 * are filled by {@link createBuilding}.
 */
type FactoryBuildingDesign = Partial<Omit<BuildingDesign, 'buildings'>> & {
  storeys?: Parameters<typeof createStoreys>[0];
  buildings?: FactoryBuilding[];
};

/**
 * Create a building design with default values if not provided
 * @param values Values to override the default values
 * @returns A new building design with the given values overriding the default values
 */
export const createBuildingDesign = (
  values: FactoryBuildingDesign = {},
  regenerateIds = true,
): BuildingDesign => {
  const { storeys, buildings, ...rest } = values;
  // Wrap storeys into a single building unless a non-empty buildings list is provided (DEV-2576).
  // Keep ids on provided buildings so tests can pass `{ id, storeys }` without extra factories.
  const resolvedBuildings = buildings?.length
    ? buildings.map((building) => createBuilding(building))
    : [{ id: v4(), storeys: createStoreys(storeys, regenerateIds) }];

  return ensureDesignHasBuilding(
    applyMissingCalculatedAreaActivities({
      location: { ...COORDINATE_STOCKHOLM },
      activities: [],
      ...omitUndefined(rest),
      buildings: resolvedBuildings,
    }),
  );
};

/**
 * True when a building has no storeys array or an empty one.
 */
const buildingNeedsStoreys = (
  building: BuildingDesign['buildings'][number],
): boolean => !building.storeys?.length;

/**
 * Guarantee at least one {@link BuildingDesign.buildings} entry, each with at
 * least one storey. Empty lists and storey-less buildings are replaced with a
 * default one-storey slot so a project cannot be saved without a floor
 * (DEV-2576). Does not call {@link createBuildingDesign} (avoids recursion).
 */
export const ensureDesignHasBuilding = (
  design: BuildingDesign,
): BuildingDesign => {
  const needsBuilding = design.buildings.length === 0;
  const needsStoreys = design.buildings.some(buildingNeedsStoreys);
  if (!needsBuilding && !needsStoreys) {
    return withPersistentBuildingNames(design);
  }

  const buildings = needsBuilding
    ? [{ id: v4(), storeys: createStoreys() }]
    : design.buildings.map((building) =>
        buildingNeedsStoreys(building)
          ? { ...building, storeys: createStoreys() }
          : building,
      );

  return withPersistentBuildingNames(
    applyMissingCalculatedAreaActivities({ ...design, buildings }),
  );
};
